import SEOHead from '../components/SEOHead';

const CONTACT_EMAIL = 'optionview580@gmail.com';
const LAST_UPDATED = 'March 4, 2026';

const H2 = 'text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2';
const H3 = 'text-base font-semibold text-neutral-800 dark:text-neutral-200 mb-1';
const LINK = 'text-primary-600 dark:text-primary-400 hover:underline';

export default function PrivacyPolicy() {
  return (
    <>
      <SEOHead
        title="Privacy Policy"
        description="Privacy Policy for OptionView — learn how we collect, use and protect your data when you use our free IV calculator for Indian options markets."
        canonicalPath="/privacy-policy"
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">Privacy Policy</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="bg-surface border border-surface rounded-clay shadow-clay dark:shadow-clay-dark backdrop-blur-sm p-6 sm:p-8 space-y-6 text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">

          <section>
            <h2 className={H2}>Introduction</h2>
            <p className="mb-2">
              Option View ("we", "us", "our") operates the website optionview.in (the "Service").
              This Privacy Policy explains what information we collect when you visit our website,
              how we use it, and what rights you have regarding your data. We are committed to
              protecting your privacy and handling your data in an open and transparent manner.
            </p>
            <p>
              By accessing or using optionview.in, you agree to the collection and use of
              information in accordance with this Privacy Policy. If you do not agree with any
              part of this policy, please discontinue use of the Service.
            </p>
          </section>

          <section>
            <h2 className={H2}>Information We Collect</h2>
            <h3 className={H3}>Analytics Data (Automatically Collected)</h3>
            <p className="mb-2">
              When you visit optionview.in, we automatically collect certain anonymised information
              through Google Analytics. This includes:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-2">
              <li>Pages you visit and time spent on each page</li>
              <li>How you arrived at the site (search engine, direct, referral)</li>
              <li>Your device type, operating system, and browser</li>
              <li>Approximate geographic location (country/city — not precise GPS)</li>
              <li>Interactions with the tool (symbol selections, chart views)</li>
            </ul>
            <p>
              This data is anonymised and aggregated. We cannot identify individual users from
              analytics data alone.
            </p>

            <h3 className={`${H3} mt-4`}>Personal Information</h3>
            <p>
              We do <strong>not</strong> collect personal information such as your name, email
              address, phone number, or financial details unless you voluntarily contact us. If
              you email us at {CONTACT_EMAIL}, we retain that correspondence solely to respond
              to your inquiry.
            </p>

            <h3 className={`${H3} mt-4`}>Cookies</h3>
            <p className="mb-2">
              Our website uses cookies — small text files stored on your device. Cookies are
              used by:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-2">
              <li>
                <strong>Google Analytics</strong> — to track site usage and user behaviour
                (anonymised)
              </li>
              <li>
                <strong>Google AdSense</strong> (when activated) — to display relevant
                advertisements based on your browsing history
              </li>
            </ul>
            <p>
              You can control or disable cookies through your browser settings. Note that
              disabling cookies may affect some functionality of the website.
            </p>
          </section>

          <section>
            <h2 className={H2}>How We Use Your Information</h2>
            <p className="mb-2">We use the information collected for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1 mb-2">
              <li>To understand how visitors use the website and improve its functionality</li>
              <li>To identify and fix technical errors or performance issues</li>
              <li>To analyse which features are most valuable to our users</li>
              <li>To display relevant advertisements via Google AdSense</li>
              <li>To respond to your emails and support requests</li>
            </ul>
            <p>
              We do <strong>not</strong> use your data for automated decision-making or profiling
              that produces legal effects concerning you.
            </p>
          </section>

          <section>
            <h2 className={H2}>Third-Party Services</h2>
            <p className="mb-2">
              We use the following third-party services that may collect and process data
              independently according to their own privacy policies:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                <strong>Google Analytics</strong> — Tracks website usage. See{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK}
                >
                  Google's Privacy Policy
                </a>
                . You can opt out using the{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK}
                >
                  Google Analytics Opt-out Browser Add-on
                </a>
                .
              </li>
              <li>
                <strong>Google AdSense</strong> — Displays personalised advertisements. See{' '}
                <a
                  href="https://policies.google.com/technologies/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK}
                >
                  Google's Advertising Privacy Policy
                </a>
                .
              </li>
            </ul>
            <p>
              We do <strong>not</strong> sell, trade, or rent your personal information to any
              third party. We do not share data with any party other than those listed above
              and only to the extent necessary to operate the Service.
            </p>
          </section>

          <section>
            <h2 className={H2}>Data Retention</h2>
            <p>
              We retain analytics data for as long as necessary to understand long-term trends
              in website usage. Email correspondence is retained for up to 12 months after the
              last interaction, unless you request deletion earlier.
            </p>
          </section>

          <section>
            <h2 className={H2}>Your Rights</h2>
            <p className="mb-2">You have the following rights regarding your data:</p>
            <ul className="list-disc pl-5 space-y-1 mb-2">
              <li><strong>Right to access</strong> — request information about what data we hold about you</li>
              <li><strong>Right to erasure</strong> — request deletion of personal data we hold</li>
              <li><strong>Right to object</strong> — object to the processing of your data for analytics or advertising</li>
              <li><strong>Right to opt out of cookies</strong> — via your browser settings or the Google opt-out tool</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className={LINK}>
                {CONTACT_EMAIL}
              </a>
              . We respond within 48 hours.
            </p>
          </section>

          <section>
            <h2 className={H2}>Children's Privacy</h2>
            <p>
              OptionView is not directed to children under the age of 18. We do not knowingly
              collect personal data from minors. If you believe a child has provided us with
              personal information, please contact us immediately and we will delete it.
            </p>
          </section>

          <section>
            <h2 className={H2}>Data Security</h2>
            <p>
              We take reasonable precautions to protect your data. Our website is served over
              HTTPS (TLS encryption). However, no method of transmission over the Internet is
              100% secure. We cannot guarantee absolute security of data transmitted to or from
              our site.
            </p>
          </section>

          <section>
            <h2 className={H2}>Legal Compliance</h2>
            <p className="mb-2">This Privacy Policy is designed to comply with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The <strong>Information Technology Act, 2000</strong> (India)</li>
              <li>The <strong>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</strong></li>
              <li>The <strong>General Data Protection Regulation (GDPR)</strong> for users in the European Union</li>
            </ul>
          </section>

          <section>
            <h2 className={H2}>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on
              this page with an updated "Last updated" date. We encourage you to review this
              page periodically. Continued use of the Service after any changes constitutes
              your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className={H2}>Contact Us</h2>
            <p className="mb-2">
              If you have any questions, concerns, or requests regarding this Privacy Policy,
              please contact us:
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className={LINK}>
                {CONTACT_EMAIL}
              </a>
              <br />
              We aim to respond within 48 hours.
            </p>
          </section>

        </div>
      </div>
    </>
  );
}
